<template>
  <div>
    <div v-if="loaded">
      <!-- Row top buttons -->
      <div class="row q-mb-lg">
        <div class="col-6" style="justify-content: flex-start">
          <q-btn
            class="capitalize ctrl-button"
            color="grey-5"
            :label="`${$store.state.stringsStore.strings.action.toOverview}`"
            icon="keyboard_backspace"
            @click="$router.push('/spare-parts')"
          />
          <span class="q-ml-md">
            <q-btn
              v-if="pdfName"
              no-caps
              icon="picture_as_pdf"
              icon-right="launch"
              :label="pdfName"
              @click="openPdf"
            />
          </span>
        </div>
        <div class="col-6">
          <q-btn
            style="float: right"
            class="col-2 capitalize ctrl-button right q-ml-md"
            :color="'ctrl-turquoise'"
            :label="`${$store.state.stringsStore.strings.action.save}`"
            @click="saveChanges()"
            icon-right="save"
          />
          <q-toggle
            style="float: right"
            class="q-pt-sm"
            v-model="data.check_status"
            left-label
            false-value="UNCHECKED"
            true-value="CHECKED"
            label="Checked"
          />
          <q-toggle
            style="float: right; margin-right:15px;"
            class="q-pt-sm"
            v-model="data.activated"
            left-label
            label="Activated"
          />
          <q-btn
            style="float: right"
            round
            color="grey-5"
            size="sm"
            class="q-mr-md"
            @click="toggleImageColumn"
            icon="remove_red_eye"
            />
        </div>
      </div>

      <!-- Columns -->
      <div class="row gutter-sm q-mb-lg">
        <!-- Left column -->
        <div class="col-md-6 col-sm-12 col-xs-12" v-show="showImageColumn">
          <div class="sticky">
            <div class="row q-mb-md">
              <div class="col-md-6">
                <q-field class="q-mr-md">
                  <q-select
                    v-if="currentView === 1"
                    float-label="Category"
                    v-model="selectedCategoryId"
                    :options="categoryOptions"
                  />
                  <q-select
                    v-else
                    float-label="List view"
                    v-model="currentListView"
                    :options="listOptions"
                  />
                </q-field>
              </div>
              <div class="col-md-6" v-if="selectedCategoryId">
                <q-field>
                  <q-select float-label="View" v-model="currentView" :options="viewOptions" />
                </q-field>
              </div>
            </div>

            <!-- Exploded view card -->
            <div class="card-block container sticky__image">
              <div class="q-pa-md full-height">
                <div class="spare-parts__view q-mb-md">
                  <div v-if="currentView === 1">
                    <img
                      :class="{ loading: !currentCategory.imageLoaded }"
                      v-on:load="mapCoordinates(currentCategory.items)"
                      :id="`explodedview-${selectedCategoryId}`"
                      :src="explodeViewUrl"
                      alt=""
                    />
                    <div
                      v-for="(coord, i) in mappedCoordinates"
                      :key="`${i}-${coord.yMin}-${imageRatio}-${currentCategory.imageLoaded}`"
                      :style="
                        `top: ${coord.yMin / imageRatio}px; left: ${coord.xMin / imageRatio}px;`
                      "
                      class="spare-parts-num"
                      :class="{ loading: !currentCategory.imageLoaded }"
                      @click="selectCoordinateFromImage(coord)"
                    >
                      {{ coord.number }}
                    </div>
                  </div>

                  <div v-else>
                    <img :src="`${base_url}${currentListView}`" alt="" />
                  </div>
                </div>

                <div class="row" v-show="currentView === 1">
                  <q-btn
                    style="float: right"
                    color="primary"
                    @click="editImage"
                    class="capitalize ctrl-button"
                    icon="edit"
                    :label="getString('module.library.button.image.edit')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="col">
          <!-- Exploded view card -->
          <div class="card-block container" v-if="currentPage && currentPage.items">
            <div class="q-pa-md full-height" v-show="!tableView">
              <div class="row q-mb-lg">
                <div class="col-md-6">
                  <q-field class="q-mr-md">
                    <q-select
                      v-if="defaultCategoryOptions.length"
                      :float-label="getString('module.spare-parts.category.select')"
                      v-model="categoryName"
                      :options="defaultCategoryOptions"
                    />
                  </q-field>
                </div>
              </div>
              <!-- autocomplete -->
              <div class="row q-mb-md">
                <div class="col row items-center q-input-chips">
                  <div
                    v-for="(filter, f) in filterArray"
                    :key="`${random}:${filter.value}`"
                    class="q-chip row no-wrap inline items-center q-chip-small bg-primary text-white"
                    tabindex="-1"
                  >
                    <div class="q-chip-main ellipsis q-popup--skip">
                      {{ filterLabels[filter.column] }} -
                      {{ filter.value ? filter.value : "Missing" }}
                    </div>
                    <div
                      class="q-chip-side q-chip-close q-chip-right row flex-center"
                      @click="removeFilter(f)"
                    >
                      <i aria-hidden="true" class="q-icon cursor-pointer material-icons">cancel</i>
                    </div>
                  </div>
                </div>
              </div>

              <!-- table -->
              <div>
                <!-- headers -->
                <div class="row q-mb-sm">
                  <div
                    class="spare-parts__table--head col-md-1"
                    :class="{ 'spare-parts__table--head-filter': itemsFilter.column === 'number' }"
                    @click="filterRows('number')"
                  >
                    Nr.
                  </div>
                  <div
                    class="spare-parts__table--head col-md-4"
                    :class="{
                      'spare-parts__table--head-filter': itemsFilter.column === 'item_code'
                    }"
                    @click="filterRows('item_code')"
                  >
                    {{ getString("module.spare-parts.field.part") }}
                  </div>
                  <div class="spare-parts__table--head col-md-6">
                    Description
                  </div>
                  <div class="spare-parts__table--head col-md-1"></div>
                </div>

                <div v-if="processingTable">
                  ...loading
                </div>

                <div v-else>
                  <div
                    v-for="(part, p) in currentPage.items"
                    :key="`${p}:${random}`"
                    class="spare-parts__table--row row q-mb-sm"
                    :class="{ 'spare-parts__table--row-new': part.new }"
                  >
                    <div class="spare-parts__table--col col-md-1">
                      <q-input
                        min="1"
                        :decimals="0"
                        v-model="part.number"
                        readonly
                        type="number"
                        @click="updateItemNumber(p)"
                        placeholder="Number"
                      />
                    </div>
                    <div
                      class="spare-parts__table--col col-md-4"
                      style="display: flex;"
                      @click="selectFilterValue(part.item_code, 'item_code')"
                    >
                      <dot-status :class="{ 'show-status--green': part.item_id !== null }">
                        {{
                          part.item_id === null
                            ? getString("module.spare-parts.part.status.unmatched")
                            : getString("module.spare-parts.part.status.matched")
                        }}
                      </dot-status>
                      <q-search type="text" v-model="part.item_code" no-icon :placeholder="'Part'">
                        <q-autocomplete
                          :max-results="25"
                          :min-characters="2"
                          @search="searchItem"
                          @selected="selectItem(part, ...arguments)"
                        />
                      </q-search>
                    </div>
                    <div class="spare-parts__table--col col-md-6">
                      <span v-if="part.name_from_db" style="line-height: 34px;">
                        {{ part.name ? part.name : "-" }}
                      </span>
                      <q-input
                        v-else
                        min="1"
                        v-model="part.name"
                        type="text"
                        placeholder="Part name"
                      />
                    </div>

                    <div
                      class="spare-parts__table--col spare-parts__table--expand col-md-1"
                      style="display: flex; justify-content: flex-end;"
                    >
                      <q-btn-group>
                        <q-btn
                          size="md"
                          no-ripple
                          style="border: none !important;"
                          @click="expandPart(part)"
                          :icon="part.expanded ? 'arrow_drop_up' : 'arrow_drop_down'"
                          :set="(referencesStatus = refStatusTooltip(part))"
                        >
                          <span class="spare-parts__reference-status">
                            <dot-status
                              :class="{
                                'show-status--orange': referencesStatus === 'found-check',
                                'show-status--green': referencesStatus === 'found'
                              }"
                            >
                              {{ getString(`module.spare-parts-refs.status-${referencesStatus}`) }}
                            </dot-status>
                          </span>
                        </q-btn>
                        <q-btn size="xs" class="ctrl-button" icon="delete" @click="removePart(p)">
                          <q-tooltip> Delete part </q-tooltip>
                        </q-btn>
                      </q-btn-group>
                    </div>

                    <transition name="fade">
                      <div class="spare-parts__table--collapse" v-if="part.expanded">
                        <div
                          class="spare-parts__table--collapse-row spare-parts__table--bordered-row row"
                        >
                          <div
                            class="spare-parts__table--collapse-col spare-parts__reference col-md-4"
                          >
                            {{ getString("module.spare-parts.field.serviceReference") }}
                          </div>
                          <div class="spare-parts__table--collapse-col col-md-4">
                            Product code
                          </div>
                          <div
                            class="spare-parts__table--collapse-col spare-parts__reference col-md-1"
                          >
                            {{ getString("module.spare-parts.field.quantity") }}
                          </div>
                          <div
                            class="spare-parts__table--collapse-col spare-parts__reference col-md-3"
                          ></div>
                        </div>

                        <div
                          class="spare-parts__table--collapse-row row"
                          v-for="(reference, r) in filterReferences(part.references)"
                          :key="`${r}:${reference.id}`"
                        >
                          <div
                            class="spare-parts__table--collapse-col spare-parts__reference col-md-4"
                          >
                            <q-search
                              type="text"
                              v-model="reference.label"
                              no-icon
                              :placeholder="'Reference'"
                            >
                              <q-autocomplete
                                :max-results="25"
                                :min-characters="2"
                                @search="searchReference"
                                @selected="selectReference(reference, ...arguments)"
                              />
                            </q-search>
                          </div>

                          <div
                            class="spare-parts__table--collapse-col col-md-4"
                            @click="
                              selectFilterValue(reference.product_code, 'reference.product_code')
                            "
                          >
                            <div></div>
                            <q-search
                              type="text"
                              v-model="reference.product_code"
                              no-icon
                              :placeholder="'Product'"
                            >
                              <q-autocomplete
                                :max-results="25"
                                :min-characters="2"
                                @search="searchItem(...arguments, false)"
                                @selected="selectProductItem(reference, ...arguments)"
                              />
                            </q-search>
                          </div>

                          <div
                            class="spare-parts__table--collapse-col spare-parts__reference col-md-1"
                          >
                            <q-input
                              align="right"
                              min="1"
                              :decimals="0"
                              v-model="reference.quantity"
                              type="number"
                              placeholder="Quantity"
                            />
                          </div>

                          <div
                            class="spare-parts__table--col col-md-3"
                            style="display: flex; justify-content: flex-end; padding-right: 10px;"
                          >
                            <q-btn-group>
                              <q-btn
                                size="xs"
                                v-if="part.references.length > 1"
                                @click="applyQuantityToAll(part.references, reference.quantity)"
                                class="ctrl-button"
                                icon="control_point_duplicate"
                              >
                                <q-tooltip>
                                  Apply quantity to all
                                </q-tooltip>
                              </q-btn>
                              <q-btn
                                size="xs"
                                @click="copyReference(reference, part)"
                                class="ctrl-button"
                                icon="file_copy"
                              >
                                <q-tooltip>
                                  Duplicate
                                </q-tooltip>
                              </q-btn>
                              <q-btn
                                size="xs"
                                @click="removeReference(r, part)"
                                class="ctrl-button"
                                icon="delete"
                              >
                                <q-tooltip>
                                  Delete
                                </q-tooltip>
                              </q-btn>
                            </q-btn-group>
                          </div>
                        </div>

                        <div
                          class="spare-parts__table--collapse-row spare-parts__table--bordered-row row"
                        >
                          <div class="spare-parts__table--col col-md-2">
                            <q-btn
                              round
                              size="sm"
                              @click="createNewReference(part)"
                              color="positive"
                              icon="add"
                            />
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>

              </div>

            </div>

            <SparePartsList
              v-if="tableView"
              :key="`${currentPage.id}:${currentPage.items}`"
              :parts="currentPage.items"
              @updatePartNumber="updateItemNumber"
              @updated="updateItems" />

            <div class="spare-parts__table--row row q-mb-sm">
              <div class="spare-parts__table--col col-md-1">
                <q-input
                  v-if="newItem.number"
                  min="1"
                  :decimals="0"
                  v-model="newItem.number"
                  type="number"
                  placeholder="Number"
                />

                <q-btn
                  v-else
                  round
                  :disabled="!newItem.item_code"
                  @click="createNewItem"
                  class="ctrl-button"
                  color="positive"
                  icon="add"
                />
              </div>
              <div class="spare-parts__table--col">
                <q-search
                  type="text"
                  v-model="newItem.item_code"
                  no-icon
                  :placeholder="getString(null, 'Search for a part', 'Onderdeel zoeken')"
                  @paste.prevent="onNewItemPaste"
                >
                  <q-autocomplete
                    ref="partAutocomplete"
                    :max-results="25"
                    :min-characters="2"
                    @search="searchItem"
                    @selected="selectItem(newItem, ...arguments)"
                  />
                </q-search>
              </div>
              <div class="spare-parts__table--col col-md-1"></div>
            </div>

            <q-btn
              no-caps
              color="grey-5"
              class="capitalize ctrl-button q-mt-md"
              :label="getString(null, 'Switch view', 'Weergave wisselen')"
              @click="tableView = !tableView"
            />
          </div>
        </div>
      </div>

      <div v-if="loaded && $store.state.meStore.me.su" class="row gutter-sm q-mb-lg">
        <div class="col-md-6 col-sm-12 col-xs-12">
          <div class="card-block container">
            <dev-tools :pdf-data="data" :current="currentPage" />
          </div>
        </div>
      </div>
    </div>

    <div class="loader" v-else-if="!loaded && dataAvailable">
      <q-spinner color="grey-5" :size="80" />
    </div>
    <div class="loader" v-else-if="!loaded && !dataAvailable">
      {{ getString("module.spare-parts.no-available-data") }}
    </div>
    <q-modal v-model="selectNumberModal" @hide="modalClose">
      <q-modal-layout>
        <q-toolbar slot="header" color="tertiary">
          <q-toolbar-title>
            {{ getString("module.spare-parts.modal.addPart.title") }}
          </q-toolbar-title>
          <q-btn flat round dense v-close-overlay icon="close" />
        </q-toolbar>
        <div class="q-pa-lg">
          <!-- Explaination -->
          <div v-if="modalStep === 1">
            <div class="row q-mb-lg">
              <p>{{ getString("module.spare-parts.modal.addPart.info") }}</p>
            </div>

            <div class="row">
              <q-btn
                color="red-5"
                class="capitalize ctrl-button"
                :label="getString('module.spare-parts.modal.addPart.continue')"
                @click="modalStep += 1"
              />
            </div>
          </div>

          <!-- Choose number or coordinate from image -->
          <div v-if="modalStep === 2">
            <div class="spare-parts__view spare-parts__view--modal q-mb-md">
              <img
                :id="`explodedview-${selectedCategoryId}`"
                :src="`${base_url}${currentPage.exploded_view}`"
                alt=""
                @click.stop="selectCoordinate"
              />
              <div v-if="imageRatio">
                <div
                  v-for="(set, i) in mappedCoordinates"
                  :key="`${i}-${set.yMin}-${imageRatio}`"
                  :style="`top: ${set.yMin}px; left: ${set.xMin}px;`"
                  class="spare-parts-num spare-parts-num__large"
                  @click="selectPartNumber(set)"
                >
                  {{ set.number }}
                </div>
              </div>
            </div>
          </div>

          <!-- Accept number or reselect -->
          <div v-if="modalStep === 3">
            <div class="row q-mb-lg">
              <p>
                {{
                  getString("module.spare-parts.modal.addPart.confirm")
                    .replace("%number%", selectNumber)
                    .replace("%part%", newItem.item_code)
                }}
              </p>
            </div>

            <div class="row">
              <q-btn
                color="red-5"
                class="capitalize ctrl-button q-mr-md"
                :label="getString('module.spare-parts.modal.addPart.link')"
                @click="finishNumberSelect"
              />
              <q-btn
                color="red-5"
                class="capitalize ctrl-button"
                :label="getString('module.spare-parts.modal.addPart.reselect')"
                @click="modalStep -= 1"
              />
            </div>
          </div>

          <!-- Add new coordinate -->
          <div v-if="modalStep === 4">
            <div class="row q-mb-lg">
              <p>You have selected a point. Please enter a number.</p>
            </div>
            <div class="row q-mb-lg">
              <q-input type="number" :decimals="0" v-model="selectNumber" float-label="Number" />
            </div>

            <div class="row">
              <q-btn
                color="red-5"
                class="capitalize ctrl-button q-mr-md"
                :label="getString('module.spare-parts.modal.addPart.link')"
                @click="addCoordinate"
              />
              <q-btn
                color="red-5"
                class="capitalize ctrl-button"
                :label="getString('module.spare-parts.modal.addPart.reselect')"
                @click="modalStep = 2"
              />
            </div>
          </div>
        </div>
      </q-modal-layout>
    </q-modal>

    <q-modal v-model="selectFromImageModal" @hide="modalClose">
      <q-modal-layout>
        <q-toolbar slot="header" color="tertiary">
          <q-toolbar-title>
            Coordinate with number {{ selectedCoordinate.number }}
          </q-toolbar-title>
          <q-btn flat round dense v-close-overlay icon="close" />
        </q-toolbar>
        <div class="q-pa-lg">
          <!-- Explaination -->
          <div>
            <div class="row q-mb-lg">
              <p>Delete coordinate or add a new reference?</p>
            </div>

            <div class="row">
              <q-btn
                color="red-5"
                class="capitalize ctrl-button q-mr-md"
                :label="getString('action.delete')"
                @click="removeCoordinate"
              />
              <q-btn
                color="positive"
                class="capitalize ctrl-button"
                :label="getString('action.add')"
                @click="addItemFromImage"
              />
            </div>
          </div>
        </div>
      </q-modal-layout>
    </q-modal>
  </div>
</template>

<script>
/* eslint-disable vue/no-side-effects-in-computed-properties */
import qs from 'qs';
import _get from 'lodash.get';
import getString from '@/helpers/GetStrings';
import DotStatus from '@/components/status/DotStatus.vue';
import DevTools from '@/components/spare-parts/DevTools.vue';

import SparePartsList from './SparePartsList.vue';

export default {
  components: {
    DotStatus,
    SparePartsList,
    DevTools,
  },
  data() {
    return {
      tableView: true,
      modalStep: 1,
      partSelected: null,
      expandId: null,
      referenceFilter: false,
      productFilter: false,
      productFilterValues: {
        nl: [
          'Toon alleen referenties met product gekoppeld',
          'Toon alleen referenties zonder product gekoppeld',
          'Toon alle referenties',
        ],
        en: [
          'Show only references with product linked',
          'Show only references without product linked',
          'Show all references',
        ],
      },
      dataAvailable: true,
      currentView: 1,
      checked: false,
      loaded: false,
      edit: false,
      selectedCategoryId: null,
      data: {},
      base_url: process.env.VUE_APP_API_URL,
      defaultCategoryOptions: [],
      viewOptions: [
        { label: this.getString('module.spare-parts.view.exploded'), value: 1 },
        { label: this.getString('module.spare-parts.view.list'), value: 2 },
      ],
      listOptions: [],
      listViewPages: [],
      pdfItems: [],
      newItem: {
        new: true,
        expanded: true,
        id: null,
        item_code: '',
        item_id: '',
        number: '',
        list_view_page: this.currentListViewPage,
        quantity: 1,
        references: [],
        coordinates: [],
      },
      newReference: {
        id: null,
        label: '',
        product_code: '',
        quantity: 1,
      },
      filterLabels: {
        number: 'Nr.',
        part: getString('module.spare-parts.field.part'),
        item_code: getString('module.spare-parts.field.part'),
        'reference.product_code': 'Product',
        quantity: getString('module.spare-parts.field.quantity'),
        'reference.label': getString('module.spare-parts.field.serviceReference'),
      },
      removeItems: [],
      sortKey: 'reference',
      sortDirection: 1,
      sorting: false,
      mappedCoordinates: [],
      coordsById: [],
      imageRatio: null,
      currentListView: null,
      offsetX: 0,
      offsetY: 0,
      currentCategory: {},
      selectNumberModal: false,
      updateNumberModal: false,
      selectNumber: null,
      newCoordinate: {
        x: 0,
        y: 0,
      },
      newCoordinatesArray: [],
      selectFromImageModal: false,
      selectedCoordinate: {
        number: null,
        id: null,
      },
      coordinatesToDelete: [],
      itemsFilter: {
        column: null,
        value: '',
      },
      filterArray: [],
      unfiltered: [],
      processingTable: false,
      random: '',
      currentPage: {},
      returnToCategoryIndex: this.$store.state.sparepartsStore.categoryIndex,
      allReferences: [],
      referenceSubstringsMatched: [],
      showImageColumn: true,
    };
  },
  methods: {
    getString,
    toggleImageColumn() {
      this.showImageColumn = !this.showImageColumn;
    },

    onNewItemPaste(evt) {
      evt.target.value = evt.clipboardData.getData('text').replaceAll(' ', '');
      const event = new Event('input');
      evt.target.dispatchEvent(event);
    },
    openPdf() {
      const site = process.env.VUE_APP_SITE;
      const url = `${site}storage/PAR/${this.pdfName}`;
      const win = window.open(url, '_blank');
      win.focus();
    },
    applyQuantityToAll(references, quantity) {
      // eslint-disable-next-line no-return-assign
      references.forEach(r => (r.quantity = quantity));
    },

    filterReferences(references) {
      return references.filter(ref => this.displayItem(ref));
    },

    updateItems(items) {
      this.currentPage.items = items;
    },

    /**
     *
     */
    displayItem(ref) {
      let display = true;
      if (this.productFilter && ref.product_code === null) {
        display = false;
      }
      if (this.productFilter === null && ref.item_id) {
        display = false;
      }
      return display;
    },

    expandPart(part) {
      part.expanded = !part.expanded;
    },

    createNewReference(part) {
      part.references.push(this.newReference);
      this.newReference = {
        id: null,
        label: '',
        product_code: '',
        quantity: 1,
      };
    },

    selectCoordinateFromImage(coord) {
      this.selectFromImageModal = true;
      this.selectedCoordinate = coord;
    },

    removeCoordinate() {
      this.coordinatesToDelete.push(this.selectedCoordinate);
      this.mappedCoordinates = this.mappedCoordinates.filter(
        c => c.id !== this.selectedCoordinate.id,
      );
      this.selectedCoordinate = {
        number: null,
        id: null,
      };
      this.selectFromImageModal = false;
    },

    deleteCoordinate(coordinate) {
      const toRemove = {
        spare_part_item_id: this.coordsById[coordinate.id],
        coordinates_id: coordinate.id,
      };
      this.axios.delete('spareparts/coordinates', { data: toRemove }).then((response) => {
        if (response.status === 204) {
          this.coordinatesToDelete = [];
        }
      });
    },

    addItemFromImage() {
      this.newItem.list_view_page = this.currentListViewPage;
      this.newItem.number = this.selectedCoordinate.number;
      this.newItem.expanded = true;
      this.newItem.coordinates = [
        {
          xMin: this.selectedCoordinate.xMin + 1 * this.currentCategory.x_offset,
          xMax: this.selectedCoordinate.xMax + 1 * this.currentCategory.x_offset,
          yMin: this.selectedCoordinate.yMin + 1 * this.currentCategory.y_offset,
          yMax: this.selectedCoordinate.yMax + 1 * this.currentCategory.y_offset,
        },
      ];
      this.addItem();
      this.selectedCoordinate = {
        number: null,
        id: null,
      };
      this.selectFromImageModal = false;
    },
    modalClose() {
      this.selectNumber = null;
      this.modalStep = 1;
      this.newItem = {
        new: true,
        id: null,
        item_code: '',
        item_id: '',
        number: '',
        list_view_page: this.currentListViewPage,
        quantity: 1,
        reference: {
          id: null,
          label: '',
        },
        references: [],
        coordinates: [],
      };
      this.partSelected = null;
      this.updateNumberModal = false;
    },

    finishNumberSelect() {
      if (this.updateNumberModal && this.partSelected >= 0) {
        this.currentPage.items[this.partSelected].number = this.selectNumber;
        this.currentPage.items[this.partSelected].coordinates = this.mappedCoordinates.filter(
          el => el.number === this.selectNumber,
        );
        this.modalClose();
      } else {
        this.newItem.number = this.selectNumber;
        this.addItem();
      }
      this.selectNumberModal = false;
    },

    selectPartNumber(part) {
      this.selectNumber = part.number;
      if (!this.newItem.item_code && !this.updateNumberModal) {
        this.newItem.item_code = part.item_code;
      }
      this.modalStep = 3;
    },

    selectCoordinate(e) {
      const rect = e.target.getBoundingClientRect();
      this.newCoordinate = {
        x: e.x - rect.left,
        y: e.y - rect.top,
      };
      this.modalStep = 4;
    },

    addCoordinate() {
      const item = this.updateNumberModal
        ? this.currentPage.items[this.partSelected]
        : this.newItem;
      item.number = null;
      this.newCoordinatesArray.push({
        xMin: this.newCoordinate.x - 20,
        xMax: this.newCoordinate.x,
        yMin: this.newCoordinate.y - 20,
        yMax: this.newCoordinate.y,
        id: null,
        number: this.selectNumber,
        item_id: item.item_id,
        item_code: item.item_code,
      });
      const newCoords = this.applyCoordOffset({
        xMin: this.newCoordinate.x - 20,
        xMax: this.newCoordinate.x,
        yMin: this.newCoordinate.y - 20,
        yMax: this.newCoordinate.y,
      });

      if (item.coordinates && Array.isArray(item.coordinates)) {
        item.coordinates.push(newCoords);
      } else {
        item.coordinates = [newCoords];
      }

      this.mappedCoordinates
        .filter(el => el.number === this.selectNumber)
        .forEach((coordinate) => {
          item.coordinates.push(this.applyCoordOffset(coordinate));
        });

      this.mappedCoordinates = [...this.mappedCoordinates, ...this.newCoordinatesArray];
      this.pdfItems
        .filter(c => c.id === this.selectedCategoryId)[0]
        .items.forEach((el) => {
          if (el.number === this.selectNumber) {
            if (Array.isArray(el.coordinates)) el.coordinates.push(newCoords);
            else el.coordinates = [newCoords];
          }
        });
      this.finishNumberSelect();
    },
    updateItemNumber(partSelected) {
      this.partSelected = partSelected;
      this.updateNumberModal = true;
      this.modalStep = 2;
      this.selectNumberModal = true;
      this.selectNumber = this.currentPage.items[this.partSelected].number;
    },
    createNewItem() {
      this.selectNumberModal = true;
    },
    applyCoordOffset(coords, reverse = false) {
      return {
        xMin: coords.xMin + (reverse ? -1 : 1 * this.currentCategory.x_offset),
        xMax: coords.xMax + (reverse ? -1 : 1 * this.currentCategory.x_offset),
        yMin: coords.yMin + (reverse ? -1 : 1 * this.currentCategory.y_offset),
        yMax: coords.yMax + (reverse ? -1 : 1 * this.currentCategory.y_offset),
      };
    },
    addItem(index = null) {
      const category = this.pdfItems.filter(c => c.id === this.selectedCategoryId)[0];
      const references = {};
      category.items.forEach((item) => {
        item.references.forEach((ref) => {
          if (!(ref.id in references)) {
            references[ref.id] = JSON.parse(JSON.stringify(ref));
            references[ref.id].quantity = 0;
          }
        });
      });
      this.newItem.references = Object.values(references);
      this.newItem.list_view_page = this.currentListViewPage;
      this.newItem.item_id = this.newItem.item_id === '' ? null : this.newItem.item_id;
      if (index !== null) {
        category.items.splice(index, 0, this.newItem);
      } else {
        category.items.push(this.newItem);
      }
      this.newItem = {
        new: true,
        expanded: false,
        id: null,
        item_code: '',
        item_id: null,
        list_view_page: this.currentListViewPage,
        number: '',
        quantity: 1,
        references: [],
        coordinates: [],
      };
    },

    copyPart(part, index) {
      this.newItem = JSON.parse(JSON.stringify(part));
      this.newItem.new = true;
      delete this.newItem.id;
      this.addItem(index);
    },

    copyReference(reference, part) {
      const newReference = JSON.parse(JSON.stringify(reference));
      newReference.id = null;
      part.references.push(newReference);
    },

    removeReference(index, part) {
      part.references.splice(index, 1);
    },
    removePart(index) {
      this.currentPage.items.splice(index, 1);
    },
    removeItem(index) {
      const category = this.pdfItems.filter(c => c.id === this.selectedCategoryId)[0];
      const item = category.items[index];
      this.removeItems.push(item);
      category.items.splice(index, 1);
    },

    editImage() {
      this.$router.push(`/spare-parts/${this.$route.params.id}/image/${this.currentPage.id}`);
    },

    async saveChanges() {
      this.filterArray = [];
      await Promise.all(this.coordinatesToDelete.map(c => this.deleteCoordinate(c)));
      const categories = this.pdfItems.map(category => JSON.stringify(this.reconstruct(category)));
      this.axios
        .put(
          `spareparts/pdf/${this.$route.params.id}`,
          qs.stringify(
            {
              check_status: this.data.check_status,
              activated: Number(this.data.activated),
              spare_parts: categories,
            },
            { arrayFormat: 'brackets', indices: false },
          ),
        )
        .then(() => {
          this.$q.notify({
            message: 'Succefully saved the PDF',
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
          this.loaded = false;
          this.getDetail();
        })
        .catch(() => {
          this.$q.notify({
            message: 'Unable to save the PDF',
            type: 'warning',
            timeout: 1200,
            position: 'top-right',
          });
        });
    },

    filterRows(column) {
      const current = this.filterArray.map(filter => filter.column);
      if (current.includes(column)) return;

      this.itemsFilter.column = column;
    },

    removeFilter(index) {
      this.filterArray.splice(index, 1);
      this.random += `${Math.random()}`;
    },

    selectFilterValue(val, col) {
      this.itemsFilter.value = val;
      if (this.itemsFilter.column === col) {
        this.filterArray.push(JSON.parse(JSON.stringify(this.itemsFilter)));
        this.itemsFilter = {
          column: null,
          value: '',
        };
        // create a random number so that the view is updated when the filter array is updated
        this.random += `${Math.random()}`;
      }
    },

    // Set the ratio of the original image size to the size of the scaled image.
    setImageRatio() {
      return new Promise((resolve) => {
        const image = document.querySelector(`#explodedview-${this.selectedCategoryId}`);
        if (!image) {
          setTimeout(async () => {
            await this.setImageRatio();
          }, 200);
        } else {
          this.imageRatio = image.naturalWidth / image.width;
          resolve();
        }
      });
    },

    mapCoordinates(items) {
      items = items || [];
      this.mappedCoordinates = [...this.newCoordinatesArray];
      items.forEach((item) => {
        if (!item.coordinates) return;
        item.coordinates.forEach((c) => {
          if (!Array.isArray(this.coordsById[c.id])) this.coordsById[c.id] = [item.id];
          else this.coordsById[c.id].push(item.id);
          if (this.mappedCoordinates.includes(cMapped => cMapped.id === c.id)) return;
          const coord = this.applyCoordOffset(c, true);
          coord.id = c.id;
          coord.number = item.number;
          this.mappedCoordinates.push(coord);
        });
      });
      this.setImageRatio();
      this.currentCategory.imageLoaded = true;
    },

    /**
     * Search for references
     */
    async searchReference(terms, done, addNew = true) {
      try {
        // Request items based on terms given
        const results = await this.axios.get(
          `spareparts/references?reference=${terms.toLowerCase()}`,
        );

        // Map only the item names/labels... to the suggested array
        const suggested = results.data.spare_part_references.map(item => ({
          label: item.reference,
          id: item.id,
        }));

        // Suggested items found, return suggestions.
        if (suggested.length) {
          done(suggested);
        }

        // Since an error is thrown when no items are found in autocomplete -> promt new suggestion here.
      } catch (error) {
        if (addNew) {
          done([{ label: `(Add) ${terms}`, value: terms, new: true }]);
        } else {
          done([]);
        }
      }
    },

    /**
     * Search for items
     */
    async searchItem(terms, done, addNew = true) {
      try {
        // Request items based on terms given
        const results = await this.axios.get(`item?code=${terms.toLowerCase()}`);

        // Map only the item names/labels... to the suggested array
        const suggested = results.data.items.map(item => ({ label: item.code, id: item.id }));

        // Suggested items found, return suggestions.
        if (suggested.length) {
          done(suggested);
        }

        // Since an error is thrown when no items are found in autocomplete -> promt new suggestion here.
      } catch (error) {
        if (addNew) {
          done([{ label: `(Add) ${terms}`, value: terms, new: true }]);
        } else {
          done([]);
        }
      }
    },

    selectReference(item, found, keyboard) {
      if (found.new) {
        found.label = found.label.replace('(Add) ', '');
        found.id = null;
      }

      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.label = found.label;
      item.id = found.id;
    },

    selectItem(item, found, keyboard) {
      if (found.new) {
        found.label = found.label.replace('(Add) ', '');
        found.id = null;
      }
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.item_code = found.label;
      item.item_id = found.id;
    },

    selectProductItem(item, found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.product_code = found.label;
      item.item_id = found.id;
    },

    async loadCategory(category) {
      this.pdfItems = [];
      return new Promise(async (resolve) => {
        this.currentListView = category.lv_public_paths
          && category.lv_public_paths[0]
          && category.lv_public_paths[0].path;

        this.pdfItems.push({
          exploded_view: category.ev_public_path,
          category_id: category.category_id,
          id: category.id,
          offsetX: category.x_offset,
          offsetY: category.y_offset,
          items: this.deconstruct(category),
          filtered: [],
        });

        this.listViewPages = category.list_view_pages;
        this.currentPage = this.pdfItems[this.$store.state.sparepartsStore.categoryIndex];
        this.currentPage = this.currentPage || this.pdfItems.filter(c => c.id === category.id)[0];
        this.currentCategory = category || {};
        this.selectedCategoryId = category.id;
        this.listOptions = category.lv_public_paths
          ? category.lv_public_paths.map(view => ({ label: `Page ${view.page}`, value: view.path }))
          : [];
        resolve();
      });
    },

    getDetail() {
      this.loaded = false;
      this.axios.get(`spareparts/pdf/${this.$route.params.id}`).then(async (response) => {
        if (response.status === 204) {
          this.dataAvailable = false;
          return;
        }
        this.data = JSON.parse(JSON.stringify(response.data.spare_part_pdf));
        await this.loadCategory(
          this.data.spare_parts[this.$store.state.sparepartsStore.categoryIndex || 0]
            || this.data.spare_parts[0],
        );
        this.loaded = true;
      });
    },

    /**
     * Take the parts list and create a new list where each item contains all part references.
     */
    deconstruct(view) {
      const newItems = [];
      this.allReferences = [];
      const items = view.items;

      if (view.items && view.items.length) {
        for (let i = 0; i < view.items.length; i += 1) {
          const part = items[i];

          const references = part && part.references;
          if (part) {
            const newPart = {
              id: part.id,
              name: part.part_name,
              name_from_db: part.part_name_is_from_db,
              item_code: part.item_code,
              item_id: part.item_id,
              remark: part.remark,
              number: part.number,
              list_view_page: part.list_view_page,
              expanded: false,
              references: [],
            };

            if (references && references.length) {
              for (let j = 0; j < references.length; j += 1) {
                const reference = references[j];
                if (reference && !newItems.includes(reference)) {
                  newPart.references.push({
                    id: reference && reference.id,
                    label: reference && reference.reference,
                    product_code: reference && reference.product_code,
                    quantity: reference && reference.quantity,
                    sort_id: reference && reference.sort_id,
                  });
                }
                if (!this.allReferences.includes(reference.reference)) {
                  this.allReferences.push(reference.reference);
                }

                // Sort the references by sort_id
                newPart.references.sort((a, b) => {
                  if (a.sort_id < b.sort_id) return -1;
                  if (a.sort_id > b.sort_id) return 1;
                  return 0;
                });
              }
            }

            newItems.push(newPart);
          }
        }
      }
      this.referenceSubstringsMatched = this.getCommonSubStrings(this.allReferences);
      return newItems;
    },
    // formats the items for saving
    reconstruct(category) {
      for (let i = 0; i < category.items.length; i += 1) {
        const item = category.items[i];
        if (!item.name_from_db) {
          item.part_name = item.name;
        }
        item.references = item.references.map(reference => ({
          reference: reference.label,
          id: reference.id,
          item_id: reference.item_id,
          quantity: reference.quantity,
          sort_id: reference.sort_id,
        }));
      }
      return category;
    },

    /**
     * This property returns the currently selected category on the pdf.
     * If sorting is used, then the items will be sorted by the chosen column.
     */
    formatCurrentPage() {
      this.processingTable = true;
      if (this.selectedCategoryId) {
        this.currentPage = this.pdfItems.filter(c => c.id === this.selectedCategoryId)[0];
        if (!this.unfiltered.length) {
          this.unfiltered = JSON.parse(JSON.stringify(this.currentPage.items));
        }
      }
      const allReferences = [];
      // Get all references from all items and remove duplicates
      this.currentPage.items.reduce((acc, item) => {
        item.references.forEach((ref) => {
          if (!acc.includes(ref.label)) {
            acc.push(ref.label);
            allReferences.push(ref);
          }
        });
        return acc;
      }, []);

      // For all items, check if all of the references in the allReferences array are present. If not, add missing references.
      this.currentPage.items.forEach((item) => {
        allReferences.forEach((ref) => {
          if (!item.references.some(r => r.label === ref.label)) {
            // Find the reference in the allReferences array and add it to the item
            const found = allReferences.find(r => r.label === ref.label);
            item.references.push({
              id: found.id,
              label: found.label,
              product_code: found.product_code,
              quantity: 0,
              sort_id: found.sort_id,
            });
          }
        });
      });

      // Sort the references by sort_id
      this.currentPage.items.forEach((item) => {
        item.references.sort((a, b) => {
          // null values should be at the end
          if (a.sort_id === null) return 1;
          if (b.sort_id === null) return -1;
          if (a.sort_id < b.sort_id) return -1;
          if (a.sort_id > b.sort_id) return 1;
          return 0;
        });
      });

      this.currentPage.items = [...this.currentPage.items, ...this.currentPage.filtered];

      // The item references should be sorted but some references may not be present on an item. I

      this.currentPage.filtered = [];
      this.filterArray.forEach((filter) => {
        this.currentPage.items = this.currentPage.items.filter((i) => {
          if (_get(i, filter.column) !== filter.value) {
            this.currentPage.filtered.push(i);
          }
          return _get(i, filter.column) === filter.value;
        });
      });

      this.processingTable = false;
    },

    getCategoryLabel(id) {
      const category = this.defaultCategoryOptions.find(c => c.value === id);
      return category ? category.label : this.getString('module.spare-parts.category.noCategory');
    },

    getLongestString(arr = []) {
      return arr.length ? arr.reduce((a, b) => (a.length > b.length ? a : b)) : arr;
    },

    getCommonSubStrings(references) {
      const matches = [];
      const aRegex = [''];
      const longest = this.getLongestString(references);

      for (let i = 0; i < references.length; i += 1) {
        for (let j = 0; j < longest.length; j += 1) {
          if (references.every(ref => ref[j] === longest[j])) {
            matches[j] = references[i][j];
          } else {
            matches[j] = null;
          }
        }
      }

      for (let i = 0; i < matches.length; i += 1) {
        const match = matches[i];
        if (match) {
          aRegex[aRegex.length - 1] += match;
        } else {
          aRegex.push('');
        }
      }

      return aRegex.filter(m => m !== '');
    },

    highlightDiff(str) {
      this.referenceSubstringsMatched.forEach((match) => {
        str = str.replace(match, `<span class="spare-parts__reference--plain">${match}</span>`);
      });
      return str;
    },
    refStatusTooltip(part) {
      let status = 'not-found';
      if (part.references.length && part.references.every(r => Number(r.quantity) === 0)) {
        status = 'found-check';
      } else if (part.references.some(r => Number(r.quantity) !== 0)) {
        status = 'found';
      }
      return status;
    },
  },
  mounted() {
    this.getDetail();
    this.setImageRatio();
    this.$root.$on('changeNav', () => {
      setTimeout(() => {
        this.imageRatio = null;
        this.setImageRatio();
      }, 500);
    });
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.imageRatio = null;
        this.setImageRatio();
      }, 500);
    });

    this.axios.get('spareparts/categories/labels').then((res) => {
      this.defaultCategoryOptions = res.data.labels.map(l => ({ label: l.label, value: l.id }));
    });
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'SparePartsImage') {
      this.$store.dispatch('updateCategoryIndex', 0);
    }
    next();
  },
  destroyed() {
    this.$root.$off('changeNav');
    window.removeEventListener('resize', null);
  },
  computed: {
    pdfName() {
      return this.data.names ? `${this.data.names[0]}.pdf` : '';
    },

    referenceFilterLabel() {
      const lang = this.$store.state.stringsStore.language;
      let val;
      switch (this.productFilter) {
        case true:
          val = 0;
          break;
        case null:
          val = 1;
          break;
        case false:
          val = 2;
          break;

        default:
          break;
      }
      return this.productFilterValues[lang][val];
    },

    currentListViewPage() {
      if (this.currentListView === undefined) {
        return -1;
      }
      const fileName = this.currentListView
        .split('\\')
        .pop()
        .split('/')
        .pop();
      const page = parseInt(fileName.match(/\d+/)[0], 10);
      return typeof page === 'string' ? -1 : page;
    },
    categoryName: {
      get() {
        return this.currentPage.category_id;
      },
      set(val) {
        this.currentPage.category_id = val;
      },
    },
    categoryOptions() {
      return this.data.spare_parts.map((view) => {
        const value = view.id;
        return {
          label: `${this.getCategoryLabel(view.category_id)} - ${this.getString(
            'module.navigation.fields.page',
          ).toLowerCase()} ${view.exploded_view_page}`,
          value,
        };
      });
    },
    explodeViewUrl() {
      return `${this.base_url}${this.currentPage.exploded_view}?${new Date().getTime()}`;
    },
  },

  watch: {
    filterArray() {
      this.formatCurrentPage();
    },
    async selectedCategoryId(val) {
      const categoryIndex = this.categoryOptions.map(c => c.value).indexOf(val);
      this.$store.commit('updateCategoryIndex', categoryIndex);
      this.currentCategory.imageLoaded = false;
      this.filterArray = [];
      this.random = '';
      this.currentCategory = this.data.spare_parts.filter(c => c.id === val)[0];
      this.imageRatio = null;
      await this.loadCategory(this.currentCategory);

      this.currentCategory.loaded = true;
      await this.setImageRatio();
    },
  },
};
</script>

<style lang="stylus" scoped>
    @import '~variables';

    .loader {
      position: absolute;
      top: calc(50% - 40px);
      left: calc(50% - 40px);
    }
    .sticky {
      position: sticky;
      top: -20px;
      transition: top 0.3s ease-in-out;
      &__image {

        overflow-y: scroll;
        max-height: calc(100vh - 180px);
      }
    }
    .spare-parts {

        &__reference-status {
          position: absolute;
          left: 1px;
          top: -2px;
        }

        &__view {
            position: relative;
            z-index: 10;

            &--modal {
              width: max-content;
              cursor: crosshair;
            }
            img {
                width: 100%;
                height: auto;
                object-fit: contain;
                border: 1px solid #F6F6F6;
                border-radius: 3px;
            }
        }

        &__table {

            &--collapse {
              width: 100%;
            }

            &--expanded {
              transform: rotate(180deg);
              transition: all 0.3s;

            };
            &--collapse-row {
              display: flex;
              flex-grow: 1;
              padding: 5px;
              border-bottom: 1px solid $ctrl-grey-lighten-1;
              background-color: #fff;
              &:nth-child(2n+1) {
                background-color: $ctrl-grey-lighten-2;
              }
              &:first-of-type {
                border-top: 1px solid $ctrl-grey-lighten-1;
              }
              &:hover:not(.spare-parts__table--bordered-row){
                background-color: $ctrl-grey-darken-1;
              }
            }
            &--bordered-row {
              border-top: 1px solid $ctrl-grey-darken-3 !important;
              border-bottom: 1px solid $ctrl-grey-darken-3;
            }
            &--collapse-col {
              display: flex;
              flex-grow: 1;
              padding: 0 5px;
            }

            &--head {
                font-weight: bold;
                cursor: pointer;
                padding: 0 5px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &--head-filter {
              color: $ctrl-blue-darken-3;
            }

            &--row {
                padding: 10px 0;
                margin: 0;
                &:nth-child(odd) {
                    background-color: $ctrl-table-striped-dark;
                }
                ::before {
                    border-style: none;
                }
            }

            &--col {
                padding: 0 5px;
            }

            &--row-new {
            }
        }
    }

.spare-parts-num {
    position: absolute;
    width: 20px;
    height: 20px;
    color: #fff;
    background: #444;
    border-radius: 50%;
    text-align: center;
    font-size: 10px;
    line-height: 20px;
    cursor: pointer;
    opacity: 1;

    transition: background 0.15s ease-in-out;

    &__large {
      width: 30px;
      height: 30px;
      font-size: 15px;
      line-height: 30px;
    }

    &:not(&--active) {
        &:hover {
            background: #000;
            z-index: 11;
        }
    }
    &--active {
        z-index: 10;
    }
}
.overlay {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9;
}
.loading {
    opacity: 0;
}
</style>
